import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Upload, Copy, Search, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PartsAdminPage = () => {
  const { token } = useAuth();
  const [partsData, setPartsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  
  // Debug function to help identify state issues  
  const debugTableState = (filteredParts) => {
    console.log('Parts Admin Debug State:', {
      partsDataLength: partsData.length,
      filteredPartsLength: filteredParts?.length || 0,
      searchTerm,
      categoryFilter,
      conditionFilter,
      partsDataSample: partsData.slice(0, 2)
    });
  };

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    part_number: '',
    condition: '',
    compatibility: '',
    description: '',
    price: '',
    availability: '',
    warranty: '',
    images: [],
    featured: false
  });

  const categories = ['Engine', 'Transmission', 'Brakes', 'Electrical', 'Cab & Body', 'Suspension'];
  const conditions = ['Genuine New', 'Reconditioned', 'Used - Excellent', 'Used - Very Good', 'Used - Good'];
  const availabilityOptions = ['In Stock', '2-3 Days', '1 Week', '2 Weeks', 'Out of Stock'];
  const warrantyOptions = ['3 months', '6 months', 'Up to 12 months'];

  // Fetch parts data with enhanced error handling
  const fetchParts = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/parts`);
      if (response.ok) {
        const data = await response.json();
        console.log('Parts data fetched successfully:', data.length, 'parts');
        
        // Validate parts data
        const validParts = data.filter(part => part && part.id);
        if (validParts.length !== data.length) {
          console.warn('Some invalid parts filtered out:', data.length - validParts.length);
        }
        
        setPartsData(validParts);
        setError(''); // Clear any previous errors
      } else {
        throw new Error(`Failed to fetch parts: ${response.status}`);
      }
    } catch (error) {
      setError('Failed to load parts data');
      console.error('Error fetching parts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParts();
  }, []);

  // Handle image upload
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    try {
      const imagePromises = files.map(async (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const base64Images = await Promise.all(imagePromises);
      setFormData({
        ...formData,
        images: [...formData.images, ...base64Images]
      });
    } catch (error) {
      console.error('Error uploading images:', error);
      setSubmitError('Failed to upload images');
    }
  };

  // Remove image
  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      // Token already available from useAuth hook
      
      const url = isEditing ? 
        `${backendUrl}/api/parts/${editingPart.id}` : 
        `${backendUrl}/api/parts`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchParts();
        setShowAddForm(false);
        setEditingPart(null);
        setIsEditing(false);
        setFormData({
          name: '',
          category: '',
          part_number: '',
          condition: '',
          compatibility: '',
          description: '',
          price: '',
          availability: '',
          warranty: '',
          images: [],
          featured: false
        });
        
        // Debug log after successful operation
        setTimeout(() => {
          console.log('Post-submission state check');
          // Will log current state after re-render
        }, 100);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save part');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(error.message);
    }
  };

  // Handle edit
  const handleEdit = (part) => {
    setFormData({
      name: part.name || '',
      category: part.category || '',
      part_number: part.part_number || '',
      condition: part.condition || '',
      compatibility: part.compatibility || '',
      description: part.description || '',
      price: part.price || '',
      availability: part.availability || '',
      warranty: part.warranty || '',
      images: part.images || [],
      featured: part.featured || false
    });
    setEditingPart(part);
    setIsEditing(true);
    setShowAddForm(true);
    // Scroll to top to show the edit form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle delete
  const handleDelete = async (partId) => {
    if (window.confirm('Are you sure you want to delete this part?')) {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        // Token already available from useAuth hook
        
        const response = await fetch(`${backendUrl}/api/parts/${partId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          await fetchParts();
        } else {
          throw new Error('Failed to delete part');
        }
      } catch (error) {
        console.error('Error deleting part:', error);
        setError('Failed to delete part');
      }
    }
  };

  // Handle duplicate
  const handleDuplicate = (part) => {
    const timestamp = Date.now();
    const newPartNumber = `${part.part_number}-COPY-${timestamp}`;
    
    const duplicateData = {
      ...part,
      id: undefined,
      part_number: newPartNumber,
      name: `${part.name} (Copy)`,
      featured: false
    };
    
    setEditingPart(null);
    setFormData(duplicateData);
    setIsEditing(false);
    setShowAddForm(true);
  };

  // Filter parts with defensive programming
  const filteredParts = partsData.filter(part => {
    // Ensure part has required properties to prevent undefined errors
    if (!part || !part.id) {
      console.warn('Invalid part found in partsData:', part);
      return false;
    }

    const searchLower = searchTerm?.toLowerCase() || '';
    const matchesSearch = !searchTerm || 
      (part.name || '').toLowerCase().includes(searchLower) ||
      (part.part_number || '').toLowerCase().includes(searchLower) ||
      (part.compatibility || '').toLowerCase().includes(searchLower);
    
    const matchesCategory = !categoryFilter || (part.category || '') === categoryFilter;
    
    // Improved condition matching to handle edge cases
    const matchesCondition = !conditionFilter || 
      (part.condition && part.condition.toLowerCase().startsWith(conditionFilter.toLowerCase()));
    
    return matchesSearch && matchesCategory && matchesCondition;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
          <p className="mt-4 text-gray-600">Loading parts admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Parts Management</h1>
              <p className="mt-1 text-sm text-gray-600">
                Current Inventory: {filteredParts.length} parts
              </p>
            </div>
            <button
              onClick={() => {
                setShowAddForm(true);
                setIsEditing(false);
                setEditingPart(null);
                setFormData({
                  name: '',
                  category: '',
                  part_number: '',
                  condition: '',
                  compatibility: '',
                  description: '',
                  price: '',
                  availability: '',
                  warranty: '',
                  images: [],
                  featured: false
                });
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add New Part
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search parts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            
            <select
              value={conditionFilter}
              onChange={(e) => setConditionFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="">All Conditions</option>
              {conditions.map(condition => (
                <option key={condition} value={condition}>{condition}</option>
              ))}
            </select>
            
            <div className="text-sm text-gray-600 flex items-center">
              Showing {filteredParts.length} of {partsData.length} parts
            </div>
          </div>
        </div>

        {/* Parts Table */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200" style={{ minWidth: '1400px' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '280px' }}>Part</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '120px' }}>Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '140px' }}>Part Number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '120px' }}>Condition</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '100px' }}>Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '120px' }}>Availability</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '140px' }}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredParts.map((part, index) => {
                  // Safety check to ensure part has required data
                  if (!part || !part.id) {
                    console.warn('Invalid part data at index:', index, part);
                    return null;
                  }
                  
                  debugTableState(filteredParts);
                  
                  return (
                  <tr key={part.id || `part-${index}`} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap" style={{ minWidth: '280px' }}>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img 
                            className="h-10 w-10 rounded object-cover" 
                            src={part.images && part.images.length > 0 
                              ? part.images[0] 
                              : "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
                            } 
                            alt={part.name || 'Part image'} 
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{part.name}</div>
                          <div className="text-sm text-gray-500">{part.compatibility}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" style={{ minWidth: '120px' }}>{part.category}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-gray-900" style={{ minWidth: '140px' }}>{part.part_number}</td>
                    <td className="px-4 py-4 whitespace-nowrap" style={{ minWidth: '120px' }}>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        part.condition === 'Genuine New' ? 'bg-green-100 text-green-800' :
                        part.condition?.startsWith('Reconditioned') ? 'bg-blue-100 text-blue-800' :
                        part.condition?.includes('Excellent') ? 'bg-purple-100 text-purple-800' :
                        part.condition?.includes('Very Good') ? 'bg-indigo-100 text-indigo-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {part.condition}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-bold" style={{ minWidth: '100px' }}>{part.price}</td>
                    <td className="px-4 py-4 whitespace-nowrap" style={{ minWidth: '120px' }}>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        part.availability === 'In Stock' ? 'bg-green-100 text-green-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {part.availability}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium" style={{ minWidth: '140px' }}>
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => handleEdit(part)}
                          className="text-orange-600 hover:text-orange-900 p-1 rounded"
                          title="Edit Part"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(part)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Duplicate Part"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(part.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Delete Part"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredParts.length === 0 && (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No parts found</h3>
                <p className="text-gray-600">Get started by adding your first part.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {isEditing ? 'Edit Part' : 'Add New Part'}
              </h3>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setEditingPart(null);
                  setIsEditing(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {submitError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-700 text-sm">{submitError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Part Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                    placeholder="e.g., MAN D2676 Complete Engine Assembly"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Part Number *</label>
                  <input
                    type="text"
                    required
                    value={formData.part_number}
                    onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                    placeholder="e.g., 51.01001-7484"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Condition *</label>
                  <select
                    required
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select Condition</option>
                    {conditions.map(condition => (
                      <option key={condition} value={condition}>{condition}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                  <input
                    type="text"
                    required
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                    placeholder="e.g., £12,500 or POA"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Availability *</label>
                  <select
                    required
                    value={formData.availability}
                    onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select Availability</option>
                    {availabilityOptions.map(availability => (
                      <option key={availability} value={availability}>{availability}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Coverage *</label>
                  <select
                    required
                    value={formData.warranty}
                    onChange={(e) => setFormData({ ...formData, warranty: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select Coverage</option>
                    {warrantyOptions.map(warranty => (
                      <option key={warranty} value={warranty}>{warranty}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Compatibility *</label>
                  <input
                    type="text"
                    required
                    value={formData.compatibility}
                    onChange={(e) => setFormData({ ...formData, compatibility: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                    placeholder="e.g., MAN TGX, TGS, TGA Series"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Detailed description of the part, condition, and any relevant information..."
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                />
                {formData.images.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Part ${index + 1}`}
                          className="w-20 h-20 object-cover rounded"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingPart(null);
                    setIsEditing(false);
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold flex items-center gap-2 transition-colors"
                >
                  <Save className="h-4 w-4" />
                  {isEditing ? 'Update Part' : 'Create Part'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white p-4 rounded-md shadow-lg">
          {error}
        </div>
      )}
    </div>
  );
};

export default PartsAdminPage;